/* ── KeyboardShortcuts ────────────────────────────────────────
   Modal overlay listing all available keyboard shortcuts.
   Opens with Cmd+/ (or Ctrl+/). Supports "Don't show again"
   via localStorage.
   ────────────────────────────────────────────────────────────── */

import { useCallback, useEffect, useRef, useState } from 'react';

const LS_HIDE_KEY = 'ch-hide-shortcuts-panel';

interface Shortcut {
  keys: string[];
  description: string;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
const mod = isMac ? '⌘' : 'Ctrl';

const SHORTCUTS: Shortcut[] = [
  { keys: [mod, 'K'],     description: 'Focus search / open command palette' },
  { keys: [mod, 'Enter'], description: 'Send query' },
  { keys: [mod, 'N'],     description: 'New chat' },
  { keys: [mod, '/'],     description: 'Show this panel' },
  { keys: [mod, 'B'],     description: 'Toggle Research sidebar (Bookmarks tab)' },
  { keys: [mod, 'H'],     description: 'Toggle Research sidebar (Highlights tab)' },
  { keys: [mod, 'E'],     description: 'Open Edition Compare' },
  { keys: [mod, ','],     description: 'Open settings' },
  { keys: ['Esc'],        description: 'Close panel / sidebar' },
  { keys: ['↑'],          description: 'Recall last query (when input empty)' },
  { keys: ['/'],          description: 'Filter templates (in command palette)' },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function KeyboardShortcuts({ open, onClose }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [hideChecked, setHideChecked] = useState(() => {
    try { return localStorage.getItem(LS_HIDE_KEY) === '1'; } catch { return false; }
  });

  /* Close on Escape */
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [open, onClose]);

  /* Close on click outside */
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  /* Persist "don't show" preference */
  const handleHideChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setHideChecked(checked);
    try {
      if (checked) localStorage.setItem(LS_HIDE_KEY, '1');
      else localStorage.removeItem(LS_HIDE_KEY);
    } catch { /* ignore */ }
  }, []);

  if (!open) return null;

  return (
    <div className="ks-overlay" onClick={handleOverlayClick}>
      <div className="ks-dialog" ref={dialogRef} role="dialog" aria-label="Keyboard shortcuts">
        <div className="ks-header">
          <h2 className="ks-title">⌨️ Keyboard Shortcuts</h2>
          <button className="ks-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="ks-body">
          <div className="ks-grid">
            {SHORTCUTS.map((s, i) => (
              <div className="ks-row" key={i}>
                <div className="ks-keys">
                  {s.keys.map((k, ki) => (
                    <span key={ki}>
                      {ki > 0 && <span className="ks-plus">+</span>}
                      <kbd className="ks-kbd">{k}</kbd>
                    </span>
                  ))}
                </div>
                <span className="ks-desc">{s.description}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="ks-footer">
          <label className="ks-hide-label">
            <input
              type="checkbox"
              checked={hideChecked}
              onChange={handleHideChange}
              className="ks-hide-checkbox"
            />
            Don't show again
          </label>
          <span className="ks-footer-hint">
            Press <kbd className="ks-kbd ks-kbd--sm">{mod}</kbd><span className="ks-plus">+</span><kbd className="ks-kbd ks-kbd--sm">/</kbd> anytime
          </span>
        </div>
      </div>
    </div>
  );
}

/** Check if the user opted to hide the shortcuts panel */
export function isShortcutsPanelHidden(): boolean {
  try { return localStorage.getItem(LS_HIDE_KEY) === '1'; } catch { return false; }
}
